import { useMemo, useState } from 'react'
import { Alert, Button, DatePicker, Descriptions, Drawer, Select, Space, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { StopOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { type Dayjs } from 'dayjs'
import { queryKeys } from '@/shared/api/queryKeys'
import { optionalRangeToIso, tenantToday } from '@/shared/lib/dayRange'
import { Can } from '@/shared/auth/Can'
import { usePermissions } from '@/shared/auth/usePermissions'
import { DataTable } from '@/shared/ui/table/DataTable'
import { PageHeader } from '@/shared/ui/PageHeader'
import { formatDateTime, utcTooltip, dash } from '@/shared/lib/format'
import { formatMoneyValue, money } from '@/shared/lib/money'
import { CancelPaymentModal } from '@/features/payments/CancelPaymentModal'
import {
  PAYMENT_STATES,
  fetchPaymentChannels,
  fetchPaymentPage,
  type Payment,
  type PaymentState,
} from '@/features/payments/paymentsApi'

const STATE_COLOR: Record<PaymentState, string> = {
  COMPLETED: 'green',
  CANCELLED: 'orange',
  FAILED: 'red',
}

/**
 * To'lovlar — operator ko'rinishi.
 *
 * <h2>Nima ko'rinadi va nima YO'Q</h2>
 * Bu ro'yxat `payments.psp_transaction` — ya'ni <b>PSP kanali orqali</b>
 * kelgan to'lovlar (Click va boshqa provayderlar). Agent kassasidan kirgan
 * pul bu yerda YO'Q va bo'lmaydi: u `ledger` jurnalida va agent
 * kabinetida. Ikkalasini bitta ro'yxatga qo'shish servislararo `JOIN`
 * talab qilardi, u esa arxitektura bo'yicha taqiqlangan.
 *
 * Shu sababli sahifa tepasida buni AYTADIGAN izoh bor: aks holda operator
 * "agent to'lovi qayerda?" deb qidirib, ro'yxatni nosoz deb hisoblardi.
 *
 * <h2>⚠️ Jami son ko'rsatilmaydi</h2>
 * Backend `total` qaytarmaydi (`hasNext` bor): `psp_transaction` cheksiz
 * o'sadi va `COUNT(*)` unda indekssiz qoladi. `showTotal={false}` —
 * majburiy, aks holda `syntheticTotal` YOLG'ON aniq son ko'rsatardi.
 */
export function PaymentsPage() {
  const { t } = useTranslation()
  const { can } = usePermissions()

  const [selected, setSelected] = useState<Payment | null>(null)
  const [cancelling, setCancelling] = useState<Payment | null>(null)
  const [state, setState] = useState<PaymentState | null>(null)
  const [pspId, setPspId] = useState<string | null>(null)
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null)

  const channels = useQuery({
    queryKey: queryKeys.payments.channels,
    queryFn: ({ signal }) => fetchPaymentChannels(signal),
    staleTime: 5 * 60_000,
  })

  const filter = useMemo(
    () => ({
      state,
      pspId,
      // ⚠️ Backend `Instant` (UTC) kutadi, DatePicker esa KALENDAR kunini
      // beradi. O'girish `dayRange.ts` da, bitta joyda — va u kunni
      // TENANT zonasida boshlaydi. Ilgari bu yerda `startOf('day')`
      // turardi, ya'ni brauzer zonasi: Kobul tenantida oyna yarim soatga
      // surilib, chegaradagi to'lovlar noto'g'ri kunga tushardi.
      ...optionalRangeToIso(range),
    }),
    [state, pspId, range],
  )

  const fetchPage = useMemo(() => fetchPaymentPage(filter), [filter])

  const columns = useMemo<ColumnsType<Payment>>(
    () => [
      {
        key: 'createdAt',
        title: t('payments.columns.createdAt'),
        width: 170,
        render: (_, row) => (
          <span title={utcTooltip(row.createdAt)}>{formatDateTime(row.createdAt)}</span>
        ),
      },
      {
        key: 'account',
        title: t('payments.columns.account'),
        width: 160,
        render: (_, row) =>
          // Abonent kartochkasiga havola faqat abonent TOPILGAN bo'lsa:
          // `subscriberId` null bo'lishi mumkin (PSP xato login yuborgan
          // va to'lov FAILED bo'lgan). Havola baribir chizilsa, u
          // `/subscribers/null` ga olib borardi.
          row.subscriberId !== null && can('subscriber.read') ? (
            <Link to={`/subscribers/${row.subscriberId}`}>{row.account}</Link>
          ) : (
            <Typography.Text>{row.account}</Typography.Text>
          ),
      },
      {
        key: 'amount',
        title: t('payments.columns.amount'),
        width: 140,
        align: 'right',
        render: (_, row) => formatMoneyValue(row.amount),
      },
      {
        key: 'channel',
        title: t('payments.columns.channel'),
        width: 150,
        render: (_, row) => <Tag>{row.channelName}</Tag>,
      },
      {
        key: 'state',
        title: t('payments.columns.state'),
        width: 130,
        render: (_, row) => (
          <Tag color={STATE_COLOR[row.state]}>{t(`paymentState.${row.state}`)}</Tag>
        ),
      },
      {
        key: 'receiptId',
        title: t('payments.columns.receipt'),
        width: 170,
        render: (_, row) => dash(row.receiptId),
      },
      {
        key: 'providerTxnId',
        title: t('payments.columns.providerTxn'),
        width: 160,
        render: (_, row) => dash(row.providerTxnId),
      },
    ],
    [t, can],
  )

  return (
    <>
      <PageHeader title={t('nav.payments')} description={t('pageIntro.payments')} />

      {/*
        ⚠️ Bu ogohlantirish KOSMETIK EMAS. Usiz operator agent kassasidan
        kirgan pulni bu ro'yxatda qidiradi, topmaydi va ro'yxatni nosoz
        deb hisoblaydi — yoki bundan ham yomoni, abonent to'lamagan deb
        xulosa qiladi.
      */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('payments.scopeNotice')}
        description={t('payments.scopeNoticeText')}
      />

      <Space wrap style={{ marginBottom: 16 }}>
        <Select<string>
          style={{ minWidth: 170 }}
          value={state ?? 'ALL'}
          onChange={(value) => setState(value === 'ALL' ? null : (value as PaymentState))}
          options={[
            { value: 'ALL', label: t('payments.filter.allStates') },
            ...PAYMENT_STATES.map((s) => ({ value: s, label: t(`paymentState.${s}`) })),
          ]}
        />
        <Select<string>
          style={{ minWidth: 190 }}
          loading={channels.isLoading}
          value={pspId ?? 'ALL'}
          onChange={(value) => setPspId(value === 'ALL' ? null : value)}
          options={[
            { value: 'ALL', label: t('payments.filter.allChannels') },
            ...(channels.data ?? []).map((c) => ({ value: c.id, label: c.name })),
          ]}
        />
        <DatePicker.RangePicker
          value={range}
          onChange={(value) =>
            setRange(value && value[0] && value[1] ? [value[0], value[1]] : null)
          }
          // Kelajakdagi sana — to'lov bo'lishi mumkin bo'lmagan oyna.
          // ⚠️ "Bugun" TENANT zonasida: `dayjs()` brauzernikini olardi va
          // Kobul tenantida operator bugungi kunni tanlay olmay qolardi.
          disabledDate={(d) => d.isAfter(tenantToday(), 'day')}
        />
      </Space>

      <DataTable<Payment>
        queryKey={[...queryKeys.payments.all, filter]}
        fetchPage={fetchPage}
        columns={columns}
        rowKey={(row) => row.id}
        tableId="payments"
        scrollX={1100}
        stickyFirstColumn
        searchable
        searchPlaceholder={t('payments.searchPlaceholder')}
        // ⚠️ MAJBURIY: backend `total` bermaydi (`pageResult.syntheticTotal`).
        showTotal={false}
        emptyTitle={t('payments.empty')}
        emptyDescription={t('payments.emptyHint')}
        onRow={(row) => ({ style: { cursor: 'pointer' }, onClick: () => setSelected(row) })}
      />

      <Drawer
        open={selected !== null}
        onClose={() => setSelected(null)}
        width={560}
        title={selected ? `${selected.account} — ${formatMoneyValue(selected.amount)}` : ''}
        extra={
          selected?.state === 'COMPLETED' ? (
            <Can permission="payment.cancel">
              <Button
                danger
                icon={<StopOutlined />}
                onClick={() => setCancelling(selected)}
              >
                {t('payments.cancel.action')}
              </Button>
            </Can>
          ) : null
        }
      >
        {selected ? (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label={t('payments.columns.state')}>
              <Tag color={STATE_COLOR[selected.state]}>
                {t(`paymentState.${selected.state}`)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('payments.columns.amount')}>
              {formatMoneyValue(selected.amount)}
            </Descriptions.Item>
            <Descriptions.Item label={t('payments.columns.channel')}>
              {selected.channelName} ({selected.channelCode})
            </Descriptions.Item>
            <Descriptions.Item label={t('payments.columns.account')}>
              {selected.account}
            </Descriptions.Item>
            <Descriptions.Item label={t('payments.columns.receipt')}>
              {dash(selected.receiptId)}
            </Descriptions.Item>
            <Descriptions.Item label={t('payments.columns.providerTxn')}>
              {dash(selected.providerTxnId)}
            </Descriptions.Item>
            {/*
              ⚠️ "To'lovdan keyingi balans" — QOTIRILGAN qiymat, hozirgi
              balans EMAS. U PSP javobining bir qismi bo'lgani uchun bir
              marta hisoblanib saqlanadi (docs/03-PSP-API.md §3.2). Buni
              yozmasak operator uni joriy balans deb o'qib, abonent
              kartochkasidagi son bilan farqini nosozlik deb hisoblardi.
            */}
            <Descriptions.Item label={t('payments.balanceAfter')}>
              <Space direction="vertical" size={0}>
                <span>
                  {selected.balanceAfter === null
                    ? t('common.dash')
                    : formatMoneyValue(money(selected.balanceAfter, selected.amount.currency))}
                </span>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t('payments.balanceAfterHint')}
                </Typography.Text>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label={t('payments.columns.paidAt')}>
              {formatDateTime(selected.paidAt)}
            </Descriptions.Item>
            <Descriptions.Item label={t('payments.processedAt')}>
              {formatDateTime(selected.processedAt)}
            </Descriptions.Item>
            {selected.errorCode ? (
              <Descriptions.Item label={t('payments.errorCode')}>
                <Tag color="red">
                  {t(`payments.rejection.${selected.errorCode}`, { defaultValue: selected.errorCode })}
                </Tag>
              </Descriptions.Item>
            ) : null}
            {/*
              Jurnal yozuvi raqami — nizoni tekshirishning kirish nuqtasi:
              operator uni `ledger` sahifasida qidiradi. Teskari yozuv
              alohida qatorda, chunki "asl" va "qaytarilgan" ni
              chalkashtirish pul tekshiruvidagi eng qimmat xato.
            */}
            <Descriptions.Item label={t('payments.ledgerTxn')}>
              {dash(selected.ledgerTxnId)}
            </Descriptions.Item>
            {selected.cancelLedgerTxnId !== null ? (
              <Descriptions.Item label={t('payments.reversalTxn')}>
                <Tag color="orange">{selected.cancelLedgerTxnId}</Tag>
              </Descriptions.Item>
            ) : null}
            {selected.cancelledAt !== null ? (
              <Descriptions.Item label={t('payments.cancelledAt')}>
                {formatDateTime(selected.cancelledAt)}
              </Descriptions.Item>
            ) : null}
            {selected.cancelReason !== null ? (
              <Descriptions.Item label={t('payments.cancelReason')}>
                {selected.cancelReason}
              </Descriptions.Item>
            ) : null}
          </Descriptions>
        ) : null}
      </Drawer>

      <CancelPaymentModal
        open={cancelling !== null}
        payment={cancelling}
        onClose={() => {
          setCancelling(null)
          setSelected(null)
        }}
      />
    </>
  )
}
